import { ButtonGroup, ButtonGroupText } from "@/components/ui/baseUi/button-group"
import { InputContainer } from "@/components/ui/forms/inputContainer"
import { InputGroup, InputGroupInput } from "@/components/ui/baseUi/input-group"
import { CouponType } from "@/modules/common/couponObject/gateway/couponObjectApiDto"
import { currencySymbol } from "@/modules/currency/currency"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { cn } from "@/modules/lib/utils"
import { type FieldValueChange } from "@/typings/formik"
import { type FieldInputProps } from "formik"
import { ChevronDownIcon } from "lucide-react"

type DiscountInputValue = {
  type: CouponType
  value: number
}

type DiscountInputProps<T, K> = {
  label?: string
  name: string
  isRequiredInput?: boolean
  setFieldValue: FieldValueChange<K>
  getFieldProps: (nameOrOptions: T) => FieldInputProps<DiscountInputValue | null>
  error?: React.JSX.Element
  className?: string
  containerClassName?: string
  isValueHidden?: boolean
  inputProps?: React.ComponentProps<"input"> & { "data-testid"?: string }
  children?: React.ReactNode
  currency?: string
} & React.ComponentProps<"div">

export function DiscountInput<T, K>({
  label,
  name,
  isRequiredInput = false,
  setFieldValue,
  getFieldProps,
  error,
  className,
  containerClassName,
  isValueHidden,
  inputProps,
  children,
  currency,
  ...props
}: DiscountInputProps<T, K>) {
  const value = getFieldProps(name as T).value
  const textValue = isValueHidden ? "" : (value?.value ?? 0)
  const language = useLanguage()

  return (
    <div className="flex flex-col gap-1">
      <InputContainer
        label={label}
        name={name}
        isRequiredInput={isRequiredInput}
        className={cn("flex flex-row items-start justify-between", className)}
        {...props}
      >
        <div className={cn("inline-flex flex-col gap-2", containerClassName)}>
          <ButtonGroup>
            <InputGroup>
              <InputGroupInput
                id="discount"
                type="number"
                value={textValue}
                inputMode="numeric"
                step={value?.type === CouponType.PERCENT ? 1 : 0.01}
                onChange={(e) => {
                  if (value?.type === CouponType.PERCENT) {
                    if (parseInt(e.target.value) >= 100) void setFieldValue(name, { type: value.type, value: 100 })
                    else if (parseInt(e.target.value) <= 0) void setFieldValue(name, { type: value.type, value: 0 })
                    else void setFieldValue(name, { type: value.type, value: parseInt(e.target.value) })
                  } else {
                    if (parseFloat(e.target.value) <= 0) void setFieldValue(name, { type: value?.type, value: 0 })
                    else void setFieldValue(name, { type: value?.type, value: parseFloat(e.target.value) })
                  }
                }}
                onBlur={getFieldProps(name as T)?.onBlur}
                className={cn(inputProps?.className ?? "")}
                {...inputProps}
              />
            </InputGroup>
            <ButtonGroupText
              onClick={() => {
                const newType = value?.type === CouponType.PERCENT ? CouponType.DIRECT : CouponType.PERCENT
                void setFieldValue(name, { type: newType, value: 0 })
              }}
            >
              {value?.type === CouponType.PERCENT ? "%" : currency ? currencySymbol(currency, language) : ""}
              <ChevronDownIcon />
            </ButtonGroupText>
          </ButtonGroup>
          {children}
        </div>
      </InputContainer>
      {error}
    </div>
  )
}
